import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBookOpenReader,
  faMagnifyingGlass,
  faRocket,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import {API_URL} from "../constants";
import authSession from "../lib/authSession";
import LearnerShell from "../components/learner/LearnerShell";
import EmptyState from "../components/learner/EmptyState";
import CourseRow from "../components/CourseRow";
import Toast from "../components/Toast";
import {getLearnerAvatar, imageUrl} from "../lib/format";

/**
 * /achievements — GET /api/achievements
 * Returns `{ user, finishedLearningPaths, finishedCourses, achievements[] }`.
 * Styling is Tailwind utilities + named theme tokens (no bootstrap classes).
 */
const Achievements = () => {
  const token = authSession.getToken();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/achievements`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch achievements");
        const payload = await response.json();
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) showToast(err.message, false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [token, showToast]);

  const achievements = useMemo(() => {
    const list = Array.isArray(data?.achievements) ? data.achievements : [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((achievement) => String(achievement?.title || "").toLowerCase().includes(term));
  }, [data, search]);

  const learnerName =
    [data?.user?.first_name, data?.user?.last_name].filter(Boolean).join(" ").trim() || "Learner";

  const statCards = [
    {label: "Paths completed", value: data?.finishedLearningPaths ?? 0, icon: faRocket},
    {label: "Courses completed", value: data?.finishedCourses ?? 0, icon: faBookOpenReader},
  ];

  return (
    <LearnerShell
      title="Achievements"
      subtitle="Everything you have completed on Fillop — proof of the work you have put in."
      icon={faTrophy}
      actions={
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search achievements…"
            aria-label="Search achievements"
            className="w-52 rounded-md border border-input bg-card py-2 pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30 sm:w-64"
          />
        </div>
      }
    >
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} /> : null}

      {isLoading && !data ? (
        <div className="space-y-4">
          <div className="h-32 animate-pulse rounded-2xl border border-border bg-muted" />
          <div className="h-64 animate-pulse rounded-2xl border border-border bg-muted" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Learner header + stats */}
          <section className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <img
                src={imageUrl(getLearnerAvatar(data?.user)) || "/images/default_profile.png"}
                alt={learnerName}
                className="h-16 w-16 shrink-0 rounded-full border border-border object-cover"
              />
              <div className="min-w-0">
                <p className="m-0 text-[11px] font-semibold uppercase tracking-wider text-accent">
                  Learner achievements
                </p>
                <h2 className="m-0 truncate text-xl font-bold text-foreground">{learnerName}</h2>
                <p className="m-0 mt-0.5 text-sm text-muted-foreground">
                  {achievements.length} achievement{achievements.length === 1 ? "" : "s"}
                  {search ? " matching your search" : ""}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {statCards.map(({label, value, icon}) => (
                <div
                  key={label}
                  className="flex min-w-[150px] items-center gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                  <span>
                    <span className="block text-lg font-bold text-foreground">{value}</span>
                    <span className="block text-xs text-muted-foreground">{label}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Achievements list */}
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h3 className="m-0 text-base font-semibold text-foreground">Your achievements</h3>
            <p className="m-0 mt-1 text-sm text-muted-foreground">
              Every course and learning path you finish lands here.
            </p>

            {achievements.length > 0 ? (
              <div className="mt-4">
                {achievements.map((achievement, index) => (
                  <CourseRow
                    key={`${achievement.title || "achievement"}-${index}`}
                    index={index}
                    {...achievement}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4">
                <EmptyState
                  icon={faTrophy}
                  title={search ? "No achievements match your search" : "No achievements yet"}
                  hint={
                    search
                      ? "Try a different search term."
                      : "Complete a course or a learning path and it will show up here."
                  }
                />
              </div>
            )}
          </section>
        </div>
      )}
    </LearnerShell>
  );
};

export default Achievements;
